import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  FileText, 
  Calendar, 
  Hammer, 
  CheckCircle,
  Shield,
  FileCheck,
  Sparkles
} from 'lucide-react';

export default function ProcessPage() {
  const steps = [
    {
      number: 1,
      icon: FileText,
      title: '$49.99 Estimate & Site Walk',
      description: 'We come to you. Walk the job. Ask questions. You get a written estimate in 48 hours—no pressure, no pushy follow-ups. At each major phase, we walk through the work together. We don\'t move to the next stage until you sign off. (Deposit will be applied to invoice after booking)',
    },
    {
      number: 2,
      icon: Calendar,
      title: 'Signed Proposal with Written Timeline',
      description: 'You\'ll know exactly what we\'re doing, when we\'re doing it, and what it costs. Detailed scope, schedule, and payment structure—nothing vague.',
    },
    {
      number: 3,
      icon: Hammer,
      title: 'Work Begins — With Updates Built In',
      description: 'We show up on time. Your project manager checks in regularly. You get proactive updates via text or email—not just when you ask.',
    },
    {
      number: 4,
      icon: CheckCircle,
      title: 'Completion',
      description: 'Punch list resolved. Site cleaned.',
    },
  ];

  const guarantees = [
    { icon: Shield, text: 'Licensed & insured' },
    { icon: FileCheck, text: 'Proper permits pulled where required' },
    { icon: Sparkles, text: 'Clean site maintained throughout the project' },
  ];

  return (
    <main data-testid="process-page" className="pt-20">
      {/* Hero */}
      <section className="bg-navy grain-overlay py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              Our Process
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mt-4 leading-tight">
              Here's Exactly What Happens When You Call Us
            </h1>
            <p className="text-white/70 font-inter text-lg mt-6 leading-relaxed">
              No mystery. No runaround. This is our process—from your first inquiry to the day we hand you the keys.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-off-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className="flex gap-8"
                data-testid={`process-step-${step.number}`}
              >
                {/* Number & Line */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-navy text-white font-playfair font-bold text-xl flex items-center justify-center">
                    {step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-0.5 h-full bg-tan/30 mt-4" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-12">
                  <div className="flex items-center space-x-3 mb-3">
                    <step.icon className="w-5 h-5 text-copper" />
                    <h3 className="text-2xl font-playfair font-bold text-navy">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-navy/70 font-inter leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-24 bg-warm-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-copper text-xs font-inter uppercase tracking-widest">
              Guarantees & Standards
            </span>
            <h2 className="text-4xl font-playfair font-bold text-navy mt-4">
              What we stand behind
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {guarantees.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 border border-tan/20 flex items-start space-x-4"
                data-testid={`guarantee-${index}`}
              >
                <item.icon className="w-6 h-6 text-copper flex-shrink-0" />
                <span className="font-inter text-navy">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy grain-overlay">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-playfair font-bold text-white">
            Ready to get started?
          </h2>
          <p className="text-white/70 font-inter mt-4">
            The first step is easy. Just tell us about your project.
          </p>
          <Link to="/contact" className="inline-block mt-8">
            <Button 
              data-testid="process-cta"
              className="bg-copper hover:bg-copper/90 text-white px-8 py-6 text-xs font-bold uppercase tracking-widest group"
            >
              Book My Estimate Now
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-white/50 font-inter text-sm mt-4">
            $49.99 (deposit will be applied to invoice after booking)
          </p>
        </div>
      </section>
    </main>
  );
}
